import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  #carouselController = new AbortController();
  #carouselDrag = null;
  #suppressCarouselClickUntil = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupBestsellersCarousel();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#carouselController.abort();
  }

  /**
   * Enables mouse dragging and vertical-wheel scrolling on the horizontal
   * bestsellers track. Native touch scrolling remains unchanged.
   */
  #setupBestsellersCarousel() {
    const track = this.querySelector('.drawer-bestsellers__track');
    if (!(track instanceof HTMLElement)) return;

    this.#carouselController.abort();
    this.#carouselController = new AbortController();
    const { signal } = this.#carouselController;

    track.addEventListener('pointerdown', this.#onCarouselPointerDown, { signal });
    track.addEventListener('pointermove', this.#onCarouselPointerMove, { signal });
    track.addEventListener('pointerup', this.#onCarouselPointerUp, { signal });
    track.addEventListener('pointercancel', this.#onCarouselPointerUp, { signal });
    track.addEventListener('click', this.#onCarouselClick, { capture: true, signal });
    track.addEventListener('dragstart', this.#onCarouselDragStart, { signal });
    track.addEventListener('wheel', this.#onCarouselWheel, { passive: false, signal });

    // Flèches prev/next : défilement au clic + masquage aux extrémités.
    const prev = this.querySelector('[data-bestsellers-prev]');
    const next = this.querySelector('[data-bestsellers-next]');
    if (prev || next) {
      const step = () => Math.max(track.clientWidth * 0.8, 160);
      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }), { signal });
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }), { signal });

      const updateArrows = () => {
        const overflow = track.scrollWidth > track.clientWidth + 1;
        const atStart = track.scrollLeft <= 1;
        const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
        if (prev instanceof HTMLElement) prev.hidden = !overflow || atStart;
        if (next instanceof HTMLElement) next.hidden = !overflow || atEnd;
      };
      track.addEventListener('scroll', updateArrows, { passive: true, signal });
      // Recalcule quand la piste devient visible (ouverture du menu) ou change de taille.
      const resizeObserver = new ResizeObserver(updateArrows);
      resizeObserver.observe(track);
      signal.addEventListener('abort', () => resizeObserver.disconnect(), { once: true });
    }
  }

  /**
   * @param {PointerEvent} event
   */
  #onCarouselPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (!(event.currentTarget instanceof HTMLElement)) return;
    if (event.currentTarget.scrollWidth <= event.currentTarget.clientWidth) return;

    this.#carouselDrag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: event.currentTarget.scrollLeft,
      track: event.currentTarget,
      moved: false,
    };
  };

  /**
   * @param {PointerEvent} event
   */
  #onCarouselPointerMove = (event) => {
    const drag = this.#carouselDrag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    // Filet de sécurité cross-navigateur : si plus aucun bouton n'est enfoncé
    // (pointerup manqué ou capture du pointeur perdue), on termine le drag tout
    // de suite. Sinon le carrousel continuerait de suivre la souris au survol,
    // une fois le clic relâché.
    if (event.buttons === 0) {
      this.#stopCarouselDrag(drag);
      return;
    }

    const delta = event.clientX - drag.startX;
    if (!drag.moved && Math.abs(delta) < 5) return;

    if (!drag.moved) {
      drag.moved = true;
      drag.track.classList.add('is-dragging');
      drag.track.setPointerCapture(event.pointerId);
    }

    event.preventDefault();
    drag.track.scrollLeft = drag.startScrollLeft - delta;
  };

  /**
   * @param {PointerEvent} event
   */
  #onCarouselPointerUp = (event) => {
    const drag = this.#carouselDrag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    if (drag.moved) {
      this.#suppressCarouselClickUntil = Date.now() + 200;
    }

    this.#stopCarouselDrag(drag);
  };

  /**
   * Termine proprement un drag du carrousel : retire l'état visuel, relâche la
   * capture du pointeur et oublie le drag en cours. Appelé au pointerup, au
   * pointercancel, et dès qu'un pointermove arrive sans bouton enfoncé.
   * @param {{ pointerId: number, track: HTMLElement }} drag
   */
  #stopCarouselDrag = (drag) => {
    drag.track.classList.remove('is-dragging');
    if (drag.track.hasPointerCapture(drag.pointerId)) {
      drag.track.releasePointerCapture(drag.pointerId);
    }
    if (this.#carouselDrag === drag) {
      this.#carouselDrag = null;
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #onCarouselClick = (event) => {
    if (Date.now() >= this.#suppressCarouselClickUntil) return;

    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * @param {DragEvent} event
   */
  #onCarouselDragStart = (event) => {
    event.preventDefault();
  };

  /**
   * @param {WheelEvent} event
   */
  #onCarouselWheel = (event) => {
    if (!(event.currentTarget instanceof HTMLElement) || event.ctrlKey) return;

    const track = event.currentTarget;
    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    const atStart = track.scrollLeft <= 0;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;

    if (!delta || (delta < 0 && atStart) || (delta > 0 && atEnd)) return;

    event.preventDefault();
    track.scrollLeft += delta;
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    // Avant l'animation du tiroir : libérer le containing block créé par le
    // transform du header (sticky scroll-up / fiche produit). Sinon le panneau
    // fixed reste calé sur ~57px et passe derrière l'image produit.
    if (details === this.refs.details) {
      this.#setHeaderMenuDrawerOpen(true);
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          this.#setHeaderMenuDrawerOpen(false);
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Flag le header pour neutraliser son transform pendant l'ouverture du menu
   * (évite le containing block qui piège le panneau fixed sur fiche produit).
   * @param {boolean} isOpen
   */
  #setHeaderMenuDrawerOpen(isOpen) {
    const header = this.closest('#header-component, .header');
    if (!(header instanceof HTMLElement)) return;

    if (isOpen) {
      header.setAttribute('data-menu-drawer-open', '');
    } else {
      header.removeAttribute('data-menu-drawer-open');
    }
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
